import { useState } from 'react';
import { Loader2, Star, Trash2, Lock } from 'lucide-react';
import { curatorLogin, fetchAllPosts, toggleEndorse, deletePost, fetchInterest, timeAgo } from '@/lib/exchangeApi';

const CuratorPage = () => {
  const [passphrase, setPassphrase] = useState('');
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [interest, setInterest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const login = async () => {
    if (!passphrase.trim()) return;
    setLoading(true);
    setError('');
    try {
      const ok = await curatorLogin(passphrase.trim());
      if (!ok) throw new Error();
      const [p, i] = await Promise.all([
        fetchAllPosts(passphrase.trim()),
        fetchInterest(passphrase.trim()),
      ]);
      setPosts(p);
      setInterest(i);
      setAuthed(true);
    } catch {
      setError('Incorrect passphrase.');
    } finally {
      setLoading(false);
    }
  };

  const onEndorse = async (id) => {
    const res = await toggleEndorse(id, passphrase.trim());
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, endorsed: res.endorsed } : p)));
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    await deletePost(id, passphrase.trim());
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  if (!authed) {
    return (
      <div className="max-w-sm mx-auto px-6 py-24" data-testid="curator-login">
        <div className="w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-5">
          <Lock size={22} />
        </div>
        <h1 className="text-2xl font-bold text-ink mb-2">Curator</h1>
        <p className="text-ink-secondary text-sm mb-6">Enter the passphrase to manage Exchange posts.</p>
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && login()}
          placeholder="Passphrase"
          data-testid="curator-passphrase-input"
          className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 mb-3"
        />
        {error && <p className="text-sm text-red-600 mb-3" data-testid="curator-error">{error}</p>}
        <button
          onClick={login}
          disabled={loading}
          data-testid="curator-login-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-teal text-white rounded-full font-bold text-sm hover:bg-brand-teal/85 transition-colors"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          Enter
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="curator-dashboard">
      <h1 className="text-3xl font-bold text-ink mb-4">Curator</h1>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 rounded-full bg-black/[0.04] p-1 self-start w-fit" data-testid="curator-tabs">
        {[
          { key: 'posts', label: `Posts · ${posts.length}` },
          { key: 'interest', label: `Interest · ${interest.length}` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`curator-tab-${t.key}`}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${
              tab === t.key ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'posts' ? (
        <div className="bg-white rounded-2xl border border-black/[0.07] overflow-hidden" data-testid="curator-posts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted border-b border-black/[0.07]">
                <th className="px-4 py-3 font-semibold">Post</th>
                <th className="px-4 py-3 font-semibold hidden sm:table-cell">By</th>
                <th className="px-4 py-3 font-semibold text-center">Votes</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} data-testid={`curator-row-${p.id}`} className="border-b border-black/[0.05] last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-ink leading-snug">{p.title}</p>
                    <span className="text-xs text-ink-muted">{p.category} · {timeAgo(p.created_at)}</span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-ink-secondary">{p.display_name} · {p.organization}</td>
                  <td className="px-4 py-3 text-center font-bold text-ink">{p.upvotes}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEndorse(p.id)}
                        data-testid={`curator-endorse-${p.id}`}
                        title={p.endorsed ? 'Remove Goodera pick' : 'Mark as Goodera pick'}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                          p.endorsed
                            ? 'bg-brand-teal border-brand-teal text-white'
                            : 'bg-white border-black/10 text-ink-secondary hover:border-brand-teal'
                        }`}
                      >
                        <Star size={12} fill={p.endorsed ? 'currentColor' : 'none'} />
                        {p.endorsed ? 'Picked' : 'Endorse'}
                      </button>
                      <button
                        onClick={() => onDelete(p.id)}
                        data-testid={`curator-delete-${p.id}`}
                        title="Delete post"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-ink-muted hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {posts.length === 0 && <p className="text-center text-ink-muted py-10">No posts yet.</p>}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/[0.07] overflow-hidden" data-testid="curator-interest-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted border-b border-black/[0.07]">
                <th className="px-4 py-3 font-semibold">Organization</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold hidden md:table-cell">Wants help with</th>
                <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">When</th>
              </tr>
            </thead>
            <tbody>
              {interest.map((s) => (
                <tr key={s.id} data-testid={`curator-interest-row-${s.id}`} className="border-b border-black/[0.05] last:border-0 align-top">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-ink leading-snug">{s.organization}</p>
                    {s.city && <span className="text-xs text-ink-muted">{s.city}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    <p className="font-medium text-ink">{s.name}{s.role && ` · ${s.role}`}</p>
                    <a href={`mailto:${s.email}`} className="text-brand-orange text-xs hover:underline">{s.email}</a>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-ink-secondary max-w-xs">{s.help_with || '—'}</td>
                  <td className="px-4 py-3 text-right text-ink-muted whitespace-nowrap">{timeAgo(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {interest.length === 0 && <p className="text-center text-ink-muted py-10">No interest submissions yet.</p>}
        </div>
      )}
    </div>
  );
};

export default CuratorPage;
